// v0.4.96 — Shared error-message normaliser.
//
// Pulled out of `OffenderActions.jsx` (where it landed as
// `toErrorString` in v0.4.94 after the Pydantic-v2-422 React
// child crash). Every catch block in the app that stores a
// network error into React state MUST route through
// `errorMessage(e)` so that a 422 detail array, a 500 dict, or
// any other non-primitive shape never reaches JSX as a raw
// object — React refuses to render objects as children, and
// the whole subtree crashes.
//
// Supported shapes:
//   - string                    → returned as-is.
//   - Array of Pydantic dicts   → "body.foo: msg; body.bar: msg".
//   - {detail: …}               → recursed into.
//   - {message: …} / {msg: …}   → unwrapped.
//   - Any other object          → JSON-stringified as a last
//                                 resort so the operator at least
//                                 sees something rather than a
//                                 crashed page.
//   - null / undefined          → "".
//
// `errorMessage(e)` is the canonical entry point — it also peels
// `e.response?.data` (axios) and `e.message` (network/timeout)
// so the catch site doesn't need to know which library raised.

export const toErrorString = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "";
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw)) {
    return raw
      .map((d) => {
        if (!d || typeof d !== "object") return String(d);
        const loc = Array.isArray(d.loc) ? d.loc.join(".") : "";
        const msg = d.msg || d.message || JSON.stringify(d);
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join("; ");
  }
  if (typeof raw === "object") {
    if (raw.message) return String(raw.message);
    if (raw.detail) return toErrorString(raw.detail);
    if (raw.msg) return String(raw.msg);
    try { return JSON.stringify(raw); } catch { return "Error"; }
  }
  return String(raw);
};

export const errorMessage = (e) =>
  toErrorString(e?.response?.data?.detail)
  || toErrorString(e?.response?.data)
  || e?.message
  || "Request failed";

export default errorMessage;
